import './error-handling-text-input.styles.scss';
import React, { useState, FC, ReactNode, useEffect } from 'react';
import { Input } from 'antd';
import { InputProps } from 'antd/lib/input';

/**
 * ErrorHandlingTextInputProps
 *
 * @memberof ErrorHandlingTextInput
 */
interface ErrorHandlingTextInputProps extends InputProps {
    /**
     * Error Message to be displayed on validation
     */
    errorMessage?: String | React.ReactElement,
    /**
     * Success Message to be displayed on validation
     */
    successMessage?: String | React.ReactElement,
    /**
     * flag for if the input field needs no validation
     */
    noValidate?: boolean,
    /**
     * Regex for validating a certian input field
     */
    validationRegex?: RegExp,
    /**
     * Validation function if dev wants to pass their own error handling
     */
    validationCallback?: Function,
    /**
     * flag for whether or not error display should be onUpdate or onBlur
     */
    updateOnInteraction?: boolean
};

/**
 * A Simple Text Input Component
 * Text input field is set to handle errors on keyboard input. 
 * Styling is changed on whether the value matches the validation method specified by the dev.
 */
export const ErrorHandlingTextInput: FC<ErrorHandlingTextInputProps> = (
    {
        errorMessage,
        successMessage,
        noValidate,
        validationRegex,
        validationCallback,
        updateOnInteraction,
        ...props
    }
) => {

    /* a regular expression that defines a basic default for an email input (test@gmail.com) */
    const defaultEmailRegex = /^([a-zA-Z0-9_\-\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9\-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$/;
    /* a regular expression that defines a basic default for an phone number input (405-123-4567) or 4051234567 */
    const defaultPhoneRegex = /^\D?(\d{3})\D?\D?(\d{3})\D?(\d{4})$/;
    /* a regular expression that defines a basic default for a url input (https://mysite.com) */
    const defaultUrlRegex = /^(http|https|ftp)\:\/\/[a-zA-Z0-9\-\.]+\.[a-zA-Z]{2,3}(:[a-zA-Z0-9]*)?\/?([a-zA-Z0-9\-\._\?\,\'/\\\+&amp;%\$#\=~])*$/;

    /* definitions of component states */
    // valid state used in error handling
    const[valid, setValid] = useState(false);
    // value of the text input field
    const[value, setValue] = useState('');
    // inputClass state for updating the styling on error or success
    const[inputClass, setInputClass] = useState('default');
    // textClass state for updating the styling for feedback text 
    const[textClass, setTextClass] = useState('default');
    // feedbackText state for the error or success message update
    const[feedbackText, setFeedbackText] = useState(successMessage);

    //placeholder text for input field
    var placeholder;

    /* sets the default attributes for props based on their input type  */
    // email defaults
    if (props.type == "email") {
        // sets default Regex if none defined
        if (!validationRegex) {
            validationRegex = defaultEmailRegex;
        }
        // sets placeholder to default or the passed in value
        props.placeholder ? placeholder = props.placeholder : placeholder = "MyEmail@gmail.com"
    }
    // phone defaults
    if (props.type == "tel") {
        // sets default Regex if none defined
        if (!validationRegex) {
            validationRegex = defaultPhoneRegex;
        }
        // sets placeholder to default or the passed in value
        props.placeholder ? placeholder = props.placeholder : placeholder = "(111) 222-3333"
    }
    // url defaults
    if (props.type == "url") {
        // sets default Regex if none defined
        if (!validationRegex) {
            validationRegex = defaultUrlRegex;
        }
        // sets placeholder to default or the passed in value
        props.placeholder ? placeholder = props.placeholder : placeholder = "https://mysite.com"
    }

    // takes place of didUpdate for functional components
    useEffect(() => {   
        validate();
    }, [value, valid])

    
    // TODO: Take a look at refactoring and reducing code redundancy using default settings
    // const defaultSettings = {
    //     email: {
    //         regex: /^([a-zA-Z0-9_\-\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9\-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$/,
    //         placeholder: "MyEmail@gmail.com"
    //     },
    //     tel: {
    //         regex: /^\D?(\d{3})\D?\D?(\d{3})\D?(\d{4})$/,
    //         placeholder: "(111) 222-3333"
    //     },
    //     url: {
    //         regex: /^(http|https|ftp)\:\/\/[a-zA-Z0-9\-\.]+\.[a-zA-Z]{2,3}(:[a-zA-Z0-9]*)?\/?([a-zA-Z0-9\-\._\?\,\'/\\\+&amp;%\$#\=~])*$/,
    //         placeholder: "https://mysite.com"
    //     }

    // }
    // if (props.type == "email" || props.type == "tel" || props.type == "url")
    // {
    //     validationRegex = validationRegex || defaultSettings[props.type].regex
    //     placeholder = placeholder || defaultSettings[props.type].placeholder
    // }

    // assigns basic default props
    const defaultedProps = Object.assign({
        placeholder: placeholder ? placeholder : 'Text Input',
        tabIndex: 0,
    }, props);
    
    /**
     * Checks whether the text input value is valid
     */
    const validate = () => {
        // doesn't validate if noValidate flagged
        if (!noValidate) {
            // checks for custom validate callback function
            if (validationCallback) {
                var callbackResponse: any;
                callbackResponse = validationCallback(value);
                // checks if validationCallback returns a promise and resolves it
                if (Promise.resolve(callbackResponse) == callbackResponse) {
                    callbackResponse.then((response:any) => {
                        setValid(response);
                    });
                } else {
                    setValid(callbackResponse);
                }
            }
            // tests against Regex if no callback is specified
            else if (validationRegex?.test(value)) {
                setValid(true);
            }
            else {
                setValid(false)
            }
        }  
        // if update on interaction it sets the classes
        if (updateOnInteraction) {
            setClasses();
        } 
    }

    /**
     * sets the classes based on whether the input field should display
     * an error, success, or default
     */
    const setClasses = () => {
        // if noValidate or noValue set to default
        if (noValidate || !value) {
            setFeedbackText('')
            setInputClass('default');
        }
        else {
            // if valid is true the classes are set to the correct state
            if (valid) {
                setInputClass('validInput');
                setTextClass('validText');
                setFeedbackText(successMessage ? successMessage : (<span style={{display: "flex"}}>
                        <i className="material-icons-round md-18">check_circle</i>
                        <span style={{position: 'relative', top:'2px'}}> Success</span>
                    </span>))
            }
            // if valid is false the classes are set to the correct state
            else {
                setInputClass('invalidInput');
                setTextClass('invalidText');
                setFeedbackText(errorMessage ? errorMessage : (<span style={{display: "flex"}}>
                        <i className="material-icons-round md-18">error</i> 
                        <span style={{position: 'relative', top:'2px'}}> Error</span>
                    </span>))
            }
        }
    }

    /**
     * Renders the component as an Ant Input 
     * component with the extra functionality assigned to it
     */
    return (
        <div className="accessible-text-input" >
        <link href="https://fonts.googleapis.com/icon?family=Material+Icons+Round" rel="stylesheet" />  
                <Input 
                    className={inputClass}
                    onChange={(e)=>{ setValue(e.target.value)}}
                    onBlur={() => {setClasses()}}
                    {...defaultedProps} >
                </Input>
                <div className={textClass} id="emailHelp">{feedbackText}</div>
        </div>
    )
};
